import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { AppShell } from '@/layouts/AppShell';
import { AgentDetailPage } from '@/views/agents/AgentDetailPage';
import { AgentsListPage } from '@/views/agents/AgentsListPage';
import { DesignSystemShowcasePage } from '@/views/designSystem/DesignSystemShowcasePage';
import { HomePage } from '@/views/home/HomePage';
import { LoginPage } from '@/views/login/LoginPage';
import { MfaPage } from '@/views/login/MfaPage';
import { SettingsPage } from '@/views/settings/SettingsPage';

/**
 * Route guard: requires authenticated session (bearer present).
 */
function RequireAuth({ children }: { children: JSX.Element }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/**
 * Redirect authenticated users away from login.
 */
function RedirectIfAuthenticated({ children }: { children: JSX.Element }) {
  const { isAuthenticated, mfaPending } = useAuth();

  if (isAuthenticated && !mfaPending) {
    return <Navigate to="/home" replace />;
  }

  return children;
}

function RootRedirect() {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated ? '/home' : '/login'} replace />;
}

/**
 * Application routes.
 */
export function AppRoutes() {
  const { mfaPending } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            {mfaPending ? <MfaPage /> : <LoginPage />}
          </RedirectIfAuthenticated>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/home" element={<HomePage />} />
        <Route path="/agents" element={<AgentsListPage />} />
        <Route path="/agents/:id" element={<AgentDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/design-system" element={<DesignSystemShowcasePage />} />
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}
